'use client';
import type { ReactElement } from 'react';
import { useContext, useMemo } from 'react';
import { useInternalNode, useStore, type Edge, type EdgeProps, type Node, type ReactFlowState, } from '@xyflow/react';
import type { FlowchartEdgeData, FlowchartNodeData } from './flowchart-types';
import { FlowchartHoverContext } from './flowchart-context';
import { FlowchartEdgeLabel } from './FlowchartEdgeLabel';
import { FlowchartEdgeMarker } from './FlowchartEdgeMarker';
import { selectLayoutCenter } from './flowchart-edge-node-model';
import { resolveFlowchartEdgeRenderModel } from './flowchart-edge-render-model';
type FlowchartFlowEdge = Edge<FlowchartEdgeData, 'flowchartDashed'>;
/** Card (not subgraph container) node type, mirrored from the layout builder. */
const FLOWCHART_CARD_NODE_TYPE = 'flowchartCard';
const MARKER_ORIENT_AUTO = 'auto';
const MARKER_ORIENT_AUTO_START_REVERSE = 'auto-start-reverse';
export function FlowchartEdge({ id, source, target, data, sourcePosition, targetPosition, }: EdgeProps<FlowchartFlowEdge>): ReactElement | null {
    const { hoveredNodeId, hoveredConnectedNodeIds, focusedNodeId, focusedNodeIds } = useContext(FlowchartHoverContext);
    const sourceNode = useInternalNode<Node<FlowchartNodeData>>(source);
    const targetNode = useInternalNode<Node<FlowchartNodeData>>(target);
    const nodeLookup = useStore((state: ReactFlowState) => state.nodeLookup);
    const layoutCenter = useMemo(() => selectLayoutCenter(nodeLookup), [nodeLookup]);
    const model = resolveFlowchartEdgeRenderModel({
        data,
        focusedNodeId,
        focusedNodeIds,
        hoveredConnectedNodeIds,
        hoveredNodeId,
        id,
        layoutCenter,
        source,
        sourceNode,
        sourcePosition,
        target,
        targetNode,
        targetPosition,
    });
    if (!model) {
        return null;
    }
    return (
      <g>
        <defs>
          <FlowchartEdgeMarker id={model.startId} kind={model.startMarker} color={model.visualState.edgeColor} orient={MARKER_ORIENT_AUTO_START_REVERSE}/>
          <FlowchartEdgeMarker id={model.endId} kind={model.endMarker} color={model.visualState.edgeColor} orient={MARKER_ORIENT_AUTO}/>
        </defs>
        <path d={model.path} fill="none" stroke={model.visualState.edgeColor} strokeOpacity={model.visualState.strokeOpacity} strokeWidth={model.visualState.strokeWidth} strokeDasharray={model.visualState.dashArray} strokeLinecap="round" strokeLinejoin="round" markerStart={model.visualState.markerStart} markerEnd={model.visualState.markerEnd} style={model.visualState.pathStyle}/>
        <FlowchartEdgeLabel color={model.visualState.edgeColor} label={model.edgeLabel} labelOpacity={model.visualState.labelOpacity} labelPosition={model.labelPosition}/>
      </g>
    );
}
